import React from 'react';

import { InfoSquareIcon } from 'assets/icons';
import Tooltip from 'components/Tooltip';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import { PERMISSIONS } from 'constants/permissions';
import { Typography } from 'antd';
import { StyledWrapper } from './styles';
import { priceTransformer } from '../../../utils';
import { WL_CONFIG } from 'constants';

const primaryColor = WL_CONFIG.colors?.primary || '#683ccf';

const TREND_PLACEHOLDER = null; // API doesn't provide prev-day comparison yet

const Overview = React.memo(({ overview }) => {
  const isAdmin = useHasAnyOfPermissions([PERMISSIONS.ADMIN_ACCESS]);

  const tiles = [
    // Row 1: GMV, Total orders, Orders in progress, Cancelled orders
    {
      key: 'gmv',
      title: 'Total GMV',
      info: 'Gross Merchandise Value (GMV) shows your total value of sales over a certain period of time.',
      value: overview?.gmv != null ? `$${Number(priceTransformer(overview.gmv, true)).toLocaleString()}` : '—',
      prominent: true,
      trend: TREND_PLACEHOLDER,
    },
    {
      key: 'total_orders',
      title: 'Total Orders',
      info: null,
      value: overview?.accepted_orders ?? overview?.total_orders ?? '—',
      prominent: false,
      trend: TREND_PLACEHOLDER,
    },
    {
      key: 'inprogress',
      title: 'Orders In Progress',
      info: 'Total number of orders excluding completed, rejected, and cancelled orders.',
      value: overview?.inprogress_orders ?? '—',
      prominent: false,
      trend: null,
    },
    {
      key: 'cancelled',
      title: 'Cancelled Orders',
      info: 'Total number of cancelled orders.',
      value: overview?.canceled_orders ?? '—',
      prominent: false,
      trend: TREND_PLACEHOLDER,
    },
    // Row 2: Subtotal, New Customers, then catering vs non-catering tiles
    {
      key: 'subtotal',
      title: 'Subtotal',
      info: 'Total food value before tax and fees.',
      value: overview?.subtotal != null ? `$${Number(priceTransformer(overview.subtotal, true)).toLocaleString()}` : '—',
      prominent: true,
      trend: TREND_PLACEHOLDER,
      adminOnly: true,
    },
    {
      key: 'new_customers',
      title: 'New Customers',
      info: 'Total number of new customers.',
      value: overview?.new_users_count ?? '—',
      prominent: false,
      trend: TREND_PLACEHOLDER,
      adminOnly: true,
    },
    // Catering orgs: Total Active Stores, Stores With Orders Today
    // Non-catering: Nibble-merchants live, Regular merchants live
    ...(overview?.is_catering
      ? [
          {
            key: 'total_active_stores',
            title: 'Total Active Stores',
            info: 'Number of stores currently accepting orders.',
            value: overview?.total_active_stores ?? '—',
            prominent: false,
            trend: TREND_PLACEHOLDER,
            adminOnly: true,
          },
          {
            key: 'stores_with_orders_today',
            title: 'Stores With Orders Today',
            info: 'Number of stores that had at least one order in the selected period.',
            value: overview?.stores_with_orders_today ?? '—',
            prominent: false,
            trend: TREND_PLACEHOLDER,
            adminOnly: true,
          },
        ]
      : [
          {
            key: 'nibble_merchants',
            title: 'Nibble-merchants live',
            info: 'Total number of Nibble merchants that are currently accepting orders.',
            value: overview?.omni_live_merchants ?? '—',
            prominent: false,
            trend: TREND_PLACEHOLDER,
            adminOnly: true,
          },
          {
            key: 'regular_merchants',
            title: 'Regular merchants live',
            info: 'Total number of non-Nibble merchants that are currently accepting orders.',
            value: overview?.non_omni_live_merchants ?? '—',
            prominent: false,
            trend: TREND_PLACEHOLDER,
            adminOnly: true,
          },
        ]),
  ].filter((t) => !t.adminOnly || isAdmin);

  return (
    <StyledWrapper>
      <div className="overview-header">
        <Typography.Title level={3}>Overview</Typography.Title>
      </div>
      <div className="tiles-grid">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className={`tile ${tile.prominent ? 'tile--prominent' : ''}`}
            style={tile.prominent ? { '--tile-accent': primaryColor } : undefined}
          >
            <div className="tile-header">
              <div className="tile-title-row">
                <span className="tile-title">{tile.title}</span>
                {tile.info && (
                  <Tooltip
                    tooltipBoxClasses="tooltip-box"
                    label={<InfoSquareIcon className="tile-info-icon" />}
                    tooltipBody={<div className="tooltip-row tooltip-previous"><span>{tile.info}</span></div>}
                    tooltipBodyClasses="tooltip"
                  />
                )}
              </div>
              <span className="tile-date">Today</span>
            </div>
            <div className="tile-value">{tile.value}</div>
            {tile.trend != null && (
              <div className="tile-trend tile-trend--growth">
                <span className="tile-trend-icon">↗</span>
                {tile.trend}
              </div>
            )}
          </div>
        ))}
      </div>
    </StyledWrapper>
  );
});

export default Overview;
